import type { RequisitionStatus } from '@/api/vocabularies';
import type { Role } from '@/api/vacancies';

/**
 * §6.8's **provenance** half — where a vacancy came from, and what that changes on screen
 * (Sprint 24, TASK-178; ADR 0020 decisions 1 and 5a).
 *
 * ⚠️ **An absent source means Panel's own.** `RoleOut.external_source` is the empty string on every
 * vacancy this product's own screens created — which is every vacancy that existed before Sprint 24
 * and every one somebody writes afterwards — so there is no second payload shape to learn and no
 * existing row to back-fill. Everything in this module is a question about one column.
 */

/**
 * Whether this vacancy is **ADP's record rather than Panel's** (ADR 0020 decision 1).
 *
 * ⚠️ **The screen is not the enforcement, and nothing here may be presented as the protection.**
 * `PATCH /api/v1/roles/{role_id}/` answers **409** on an ADP-sourced row, for **every** caller, an
 * `Admin` included — `apps.vacancies.services.update_role` refuses the whole request rather than the
 * ADP-owned keys inside it, and `backend/src/apps/vacancies/api.py` declares the response. What this
 * predicate buys is that nobody is shown a control whose only possible answer is that refusal, and
 * that the record says where it is maintained instead of leaving an absence to be read as a defect.
 *
 * It asks about the **source** and never about the status: a closed requisition is as read-only as an
 * open one, and a vacancy with no source is editable whatever its `is_open` says.
 */
export const isImportedVacancy = (role: Role): boolean => role.external_source !== '';

/**
 * ADP's five requisition statuses, **derived from the document** (ADR 0020 decision 5a).
 *
 * ⚠️ **These five tokens were hand-listed here until 2026-10-29, and the finding that said so has
 * been answered rather than deleted.** The note read that `RoleOut.requisition_status` is declared
 * `str` on the backend, so `openapi.snapshot.json` typed it `{"type": "string"}`, there was no
 * `components.schemas.RequisitionStatus` to re-export, and *"typing the field as its `TextChoices` on
 * the backend is the first line of defence and is **reported as a backend item**; until it ships,
 * this is where an unrecognised code stops"*. That item shipped: the document carries the enum, the
 * payload type is `RequisitionStatus | ''`, and `api/vocabularies.ts` re-exports it beside the three
 * vocabularies that made the same journey. The `Record` below is now **total by construction** — a
 * sixth value declared on the backend stops `tsc` here, at the one place that decides what a reader
 * is shown, rather than reaching a cell as an unrecognised token.
 *
 * **The value is whether the requisition is open**, which is also `Role.is_open` on an ADP-sourced
 * row — the backend writes the boolean as a projection of the status in the same statement, by a
 * total map over these five members. It is restated here as the **rendering**'s own question rather
 * than read off `is_open`, so that one cell reads one column: ⚠️ **§6.8 renders the status word on an
 * imported vacancy and the Open/Closed word on a hand-created one, and never both** (ADR 0020
 * decision 5a, lesson 3 — *"the disagreement the lesson fears is a reader seeing two answers"*).
 *
 * ⚠️ **`CANCELED` is not `CLOSED` and `ON_HOLD` is neither.** An abandoned search is not a finished
 * one and a paused one is not either, which is exactly why the boolean could not carry this on its
 * own: five words, five renderings, and *On hold* must never read as *Closed*.
 */
const REQUISITION_STATUS_IS_OPEN: Record<RequisitionStatus, boolean> = {
  OPEN: true,
  CLOSED: false,
  ON_HOLD: false,
  REJECTED: false,
  CANCELED: false,
};

/** The five tokens, for the test that pins the catalogue's words against the document's enum. */
export const REQUISITION_STATUSES = Object.keys(REQUISITION_STATUS_IS_OPEN) as RequisitionStatus[];

/**
 * Whether a value is one of the five — `vocabularies.ts`'s own idiom (`isConsiderationStatus`), and
 * the reason `requisitionStatusRendering` can keep taking a `string`.
 */
const isRequisitionStatus = (value: string): value is RequisitionStatus =>
  value in REQUISITION_STATUS_IS_OPEN;

/**
 * How §6.8 renders one requisition status, or `null` for a value this build has no word for.
 *
 * ⚠️ **It takes a `string` although the payload is now narrower, and that is deliberate.** The
 * document types the field `RequisitionStatus | ''`, so `tsc` says this branch cannot be reached from
 * a real response — but the branch is about what a **running** screen does with a value nobody
 * expects, and a type is not a runtime guarantee about another service's JSON. Narrowing the
 * parameter would delete the branch and the test that drives it in the same stroke.
 *
 * ⚠️ **`null` renders nothing rather than the token** — `StatusDot`'s rule, one screen over: a badge
 * that silently does not appear is a bug, and `ON_HOLD` in front of a recruiter is a different and
 * worse one. It is not a state anybody should meet: a status outside the five **imports nothing** and
 * is reported by `itemID` (ADR 0020 decision 5a), so no row carrying one can exist here — the branch
 * is what makes that true of the screen as well as of the importer.
 *
 * The emphasis follows §6.8's existing idiom exactly, and deliberately adds no colour: an open
 * requisition is plain, every other state is outlined, and **the word is what carries the state**
 * (§6.8's own 2026-07-29 correction, and §3's *"a five-colour status palette is how an interface
 * starts shouting"*).
 */
export function requisitionStatusRendering(
  status: string,
): { labelKey: string; isOpen: boolean } | null {
  if (!isRequisitionStatus(status)) return null;

  return {
    labelKey: `requisition.status.${status}`,
    isOpen: REQUISITION_STATUS_IS_OPEN[status],
  };
}
